/**
 * Orders Copilot Suggestions
 * Context-aware suggestions for the AI chat
 * Uses: store/selectors
 */

import { useMemo } from 'react'
import { useCopilotChatSuggestions } from '@copilotkit/react-ui'
import { useSelector } from '@ufc/core'
import {
  selectPendingOrdersCount,
  selectOrderStats,
} from '../store/orders.selectors'

/**
 * Generate contextual suggestions based on order state
 */
export const useOrdersSuggestions = () => {
  const pendingCount = useSelector(selectPendingOrdersCount)
  const stats = useSelector(selectOrderStats)

  const suggestions = useMemo(() => {
    const list: string[] = []

    // Suggest reviewing pending orders
    if (pendingCount > 0) {
      list.push(`Review ${pendingCount} pending order${pendingCount > 1 ? 's' : ''}`)
    }

    // Suggest checking processing orders
    if (stats.processing > 0) {
      list.push(`Check ${stats.processing} processing order${stats.processing > 1 ? 's' : ''}`)
    }

    // Suggest creating new order
    list.push('Create a new cement order')

    // Suggest generating report if many delivered
    if (stats.delivered > 10) {
      list.push('Generate delivery report')
    }
    
    return list
  }, [pendingCount, stats])

  const instructions = useMemo(() => `Based on the current orders context:
    - There are ${pendingCount} pending orders that may need attention
    - There are ${stats.processing} orders currently processing
    - Total of ${stats.total} orders in the system
    - ${stats.delivered} orders have been delivered

    Suggest helpful actions like:
    ${suggestions.map(s => `- ${s}`).join('\n')}

    Be proactive in offering to help manage orders, track deliveries, or create new orders.`, [pendingCount, stats, suggestions])

  useCopilotChatSuggestions({
    instructions,
  })
}
